import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, ArrowRight, Users, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import siteConfig from '@/config/siteConfig';

export const ForWhoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const personalBenefits = [
    "Retrouve la joie de vivre",
    "Libérer tes blocages intérieurs",
    "Reconnecte ton corps et ton âme",
    "Kiffe ta vie, pleinement incarné(e)"
  ];

  const practitionerBenefits = [
    "Intègre des outils vibratoires uniques",
    "Donne des soins profonds et éthiques",
    "Déploye ta puissance lumineuse",
    "Transforme tes accompagnements"
  ];

  const targetAudience = [
    "tu es fatigué(e) de \"comprendre\" sans que ta réalité suive",
    "tu veux arrêter de te corriger et commencer à te déployer",
    "tu veux une joie stable, pas un shoot d'inspiration",
    "tu veux une spiritualité incarnée : corps, émotion, choix, présence",
    "tu sens que tu es prêt(e) à changer de niveau (un vrai)"
  ];

  return (
    <section ref={ref} className="section-padding bg-background-secondary relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-stellar" />
      </div>

      <div className="container-luxury relative">
        {/* Two Cards Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {/* For You Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="luxury-card rounded-3xl p-8 md:p-10 relative group hover:border-accent/30 transition-all duration-500"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-50" />
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shadow-gold">
                <User className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-foreground">Pour Toi</h3>
            </div>

            <ul className="space-y-4">
              {personalBenefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex items-center gap-3 text-foreground/90 font-body"
                >
                  <span className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-accent" />
                  </span>
                  {benefit}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* For Practitioners Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="luxury-card-gold rounded-3xl p-8 md:p-10 relative group"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent-light to-transparent" />
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center shadow-gold">
                <Users className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-foreground">Pour les Praticiens</h3>
            </div>

            <ul className="space-y-4 mb-8">
              {practitionerBenefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3 text-foreground/90 font-body"
                >
                  <span className="w-6 h-6 rounded-full bg-accent/30 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-accent" />
                  </span>
                  {benefit}
                </motion.li>
              ))}
            </ul>

            <a href={siteConfig.therapistLink} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="btn-outline-gold rounded-full group">
                Thérapeute? + pour toi ici
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          {/* Intro Text */}
          <div className="glass-card rounded-3xl p-8 md:p-12 mb-12">
            <p className="text-xl md:text-2xl font-display text-foreground leading-relaxed mb-6">
              <span className="text-gradient-gold">Tu as tout pour être heureux(se) sur le papier…</span> 
              {" "}mais ça ne vibre pas autant que prévu.
            </p>
            <p className="text-muted-foreground font-body text-lg leading-relaxed mb-4">
              Ici, on ne te sert pas des concepts prémâchés.
            </p>
            <p className="text-foreground font-body text-lg leading-relaxed">
              On est sur du <span className="text-gold-luminous font-semibold">high level</span> : précis, profond, incarné. 
              Une forme de <span className="text-gold-luminous font-semibold">haute couture intérieure</span>.
            </p>
          </div>

          {/* Target Audience */}
          <div className="text-center mb-8">
            <h3 className="font-display text-2xl md:text-3xl text-foreground mb-8">
              Cette École est pour toi si :
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {targetAudience.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="flex items-start gap-4 p-5 rounded-xl bg-card/50 border border-border/30 hover:border-accent/30 transition-colors"
              >
                <span className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-accent" />
                </span>
                <p className="text-foreground/90 font-body leading-relaxed text-sm md:text-base">{point}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ForWhoSection;
